import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useRealtimeWorkflows } from '@/hooks/useRealtimeWorkflows';
import { Loader2, CheckCircle, XCircle, Clock, History, AlertTriangle, X, ChevronLeft, ChevronRight } from 'lucide-react';

export default function HistoryPage() {
  const navigate = useNavigate();
  const {
    executions,
    loading,
    error,
    cancelExecution,
    hasMoreExecutions,
    executionsPage,
    nextExecutionsPage,
    previousExecutionsPage,
  } = useRealtimeWorkflows();
  const [cancelingIds, setCancelingIds] = useState<Set<string>>(new Set());

  const statusIcon = (s: string) => {
    switch (s) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500 shrink-0" />;
      case 'error':
        return <XCircle className="h-5 w-5 text-red-500 shrink-0" />;
      case 'lost':
        return <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0" />;
      case 'canceled':
      case 'cancelled':
        return <XCircle className="h-5 w-5 text-muted-foreground shrink-0" />;
      default:
        return <Loader2 className="h-5 w-5 animate-spin text-violet-500 shrink-0" />;
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return (
      d.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }) +
      ' at ' +
      d.toLocaleTimeString(undefined, {
        hour: 'numeric',
        minute: '2-digit',
      })
    );
  };

  const handleCancelExecution = async (promptId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (window.confirm('Cancel this execution?')) {
      setCancelingIds((prev) => new Set(prev).add(promptId));
      try {
        await cancelExecution(promptId);
      } catch {
        alert('Failed to cancel execution');
      } finally {
        setCancelingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(promptId);
          return newSet;
        });
      }
    }
  };

  const getPromptFromInputs = (inputParams?: Record<string, unknown>): string | null => {
    if (!inputParams) return null;

    // Look for common prompt field names
    const promptKeys = ['prompt', 'text', 'positive', 'positive_prompt', 'input_text', 'description'];

    for (const key of promptKeys) {
      const value = inputParams[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }

    return null;
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8">
      <div className="max-w-4xl mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
            <History className="h-6 w-6" />
            Execution History
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Real-time updates on your workflow runs</p>
        </header>

        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg mb-6">
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {executions.length === 0 ? (
          <div className="text-center py-20">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-accent mb-4">
              <History className="h-8 w-8 text-muted-foreground/40" />
            </div>
            <h3 className="text-lg font-medium text-muted-foreground">No executions yet</h3>
            <p className="text-sm text-muted-foreground/60 mt-1">Run a workflow to see your history here</p>
          </div>
        ) : (
          <>
            <div className="space-y-1.5">
              {executions.map((exec) => {
                const isPending = exec.status === 'queued' || exec.status === 'running' || exec.status === 'localizing';
                const prompt = getPromptFromInputs(exec.inputParams);
                const hasPrompt = prompt !== null;
                const visibleOutputs = exec.outputs.filter((o) => !o.hidden);

                return (
                  <div key={exec.id} className="relative group">
                    <button type="button" className="w-full text-left" onClick={() => navigate(`/history/${exec.id}`)}>
                      <Card className="border-border/60 hover:bg-accent/30 transition-colors cursor-pointer">
                        <CardHeader className="py-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0">
                              {statusIcon(exec.status)}
                              <div className="min-w-0">
                                <CardTitle className="text-sm font-medium truncate">{exec.workflowName}</CardTitle>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className="text-xs text-muted-foreground">{formatDate(exec.createdAt)}</span>
                                  {exec.completedAt && (
                                    <span className="text-xs text-muted-foreground/60 flex items-center gap-0.5">
                                      <Clock className="h-3 w-3" />
                                      {Math.round(
                                        (new Date(exec.completedAt).getTime() - new Date(exec.createdAt).getTime()) / 1000,
                                      )}
                                      s
                                    </span>
                                  )}
                                  <span className="text-[10px] font-mono text-muted-foreground/40">{exec.id.slice(0, 8)}</span>
                                  {isPending && <span className="text-[10px] text-violet-500">live</span>}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {exec.status === 'completed' && visibleOutputs.length > 0 && (
                                <span className="text-[10px] text-muted-foreground">
                                  {visibleOutputs.length} {visibleOutputs.length === 1 ? 'file' : 'files'}
                                </span>
                              )}
                              <Badge
                                variant={
                                  exec.status === 'completed'
                                    ? 'default'
                                    : exec.status === 'error' || exec.status === 'lost'
                                      ? 'destructive'
                                      : exec.status === 'canceled' || exec.status === 'cancelled'
                                        ? 'outline'
                                        : 'secondary'
                                }
                                className="text-[10px]"
                              >
                                {exec.status}
                              </Badge>
                            </div>
                          </div>
                          {hasPrompt && (
                            <div className="mt-2">
                              <p className="text-xs text-muted-foreground/80 line-clamp-2 italic">"{prompt}"</p>
                            </div>
                          )}
                        </CardHeader>
                        {isPending && (
                          <CardContent className="pt-0 pb-3">
                            <div className="w-full bg-accent rounded-full h-1 overflow-hidden">
                              <div className="bg-violet-500 h-1 rounded-full animate-pulse w-1/2" />
                            </div>
                          </CardContent>
                        )}
                      </Card>
                    </button>
                    {isPending && (
                      <button
                        onClick={(e) => handleCancelExecution(exec.id, e)}
                        disabled={cancelingIds.has(exec.id)}
                        className={
                          'absolute -top-2 -right-2 w-6 h-6 rounded-full ' +
                          'bg-destructive hover:bg-destructive/90 ' +
                          'flex items-center justify-center shadow-md ' +
                          'opacity-0 group-hover:opacity-100 transition-opacity ' +
                          'disabled:opacity-50 z-10'
                        }
                        title="Cancel execution"
                      >
                        {cancelingIds.has(exec.id) ? (
                          <Loader2 className="h-3.5 w-3.5 text-destructive-foreground animate-spin" />
                        ) : (
                          <X className="h-3.5 w-3.5 text-destructive-foreground" />
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border/60">
              <div className="text-sm text-muted-foreground">Page {executionsPage + 1}</div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={previousExecutionsPage}
                  disabled={executionsPage === 0}
                  className="gap-1"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <Button variant="outline" size="sm" onClick={nextExecutionsPage} disabled={!hasMoreExecutions} className="gap-1">
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
